import React, { ChangeEvent, useState, useEffect, useMemo, useCallback } from 'react'

import {animated, useTransition } from '@react-spring/web';
import Slider from 'src/components/slider';

import { Asset } from 'src/types/assets';
import { MediaLibraryChooser } from 'src/components/mediaLibrary';
import useMeasure from 'react-use-measure';
import { MediaLibrary } from 'src/types/mediaLibrary';
import Button from 'src/components/button';
import { useSelector } from 'react-redux';
import { Crop } from 'src/components/editThumbnail/crop';
import { useDispatch } from 'react-redux';
import { parseMediaMetadata } from 'src/services/media.service';
import { invalidateMediaUpload, uploadMedia } from 'src/redux/actions/media';
import {  Media, MediaFile } from 'src/types/media';
import { updateAsset } from 'src/redux/actions/assets';
import Thumbnail from 'src/components/thumbnail';
import Tooltip from "src/components/tooltip";
import { nanoid } from 'nanoid';
import { ThumbnailType } from 'src/types/common';

import _ from "lodash";
import cx from "classnames";
import styles from "./editAssetThumbnail.module.scss";
import { toast } from 'react-toastify';
import Toast from 'src/components/toast';
import { IUseFetchProps, useFetch } from 'src/components/hooks/useFetch';
import { useAssetAdoption } from 'src/redux/actions/adoption/asset';
import { ASSET_DETAIL } from 'src/constants/adoption/sourceTypes';
import { getSectionType, ScoreCard } from 'src/pages/contentMap/contentScore/contentScore.helper';

const DefaultAssetIcon = '/images/defaultIcons/default_asset.jpg'
interface EditAssetThumbnailProps {
  asset: Asset;
  show: boolean;
  onClose: () => void;
  selectedCard?: ScoreCard | null;
}

const EditAssetThumbnail = ({asset, show, onClose, selectedCard}: EditAssetThumbnailProps) => {

  const [editThumbail, setEditThumbnail] = useState(false);
  const [editAction, setEditAction] = useState("default");
  const [selectedMedia, setSelectedMedia] = useState<Media | null> (null);
  const [cropData, setCropData] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const [selectedMediaSource, setSelectedMediaSource] = useState<string | undefined>(undefined);
  const [fileSizeError, setFileSizeError] = useState(false);

  const uploading = useSelector((state: any) => _.get(state, `asset.update.${asset.id}.uploading`, false));
  const mediaUploading = useSelector((state: any) => _.get(state, `media.${selectedMedia?.id}.loading`, false));
  const mediaUploaded = useSelector((state: any) => _.get(state, `media.${selectedMedia?.id}.loaded`, false));
  const media = useSelector((state: any) => _.get(state, `media.${selectedMedia?.id}.item`, null));
  
  const updating = useSelector((state: any) => _.get(state, `asset.update.${asset.id}.updating`, false));
  const updated = useSelector((state: any) => _.get(state, `asset.update.${asset.id}.updated`, false));

  const updatedAsset = useSelector((state: any) => _.get(state, `asset.update.${asset.id}.item`, null));

  const [headerRef, headerBounds] = useMeasure();


  const dispatch = useDispatch();


  const updateAssetProps: IUseFetchProps = {
    action: updateAsset,
    loading: updating,
    succeeded: updated,
    response: updatedAsset,
    error: null
  };

  const { handleFetch: updateAssetRequest } = useFetch(updateAssetProps);

  const { trackAssetEdited } = useAssetAdoption();


  const handleThumbnailChange = (media: MediaLibrary) => {
    setCropData({ x: 0, y: 0, width: media.width ?? 0, height: media.height ?? 0 });

    if(!media) {
      return;
    }

    const imageUrl = media.imageUrl;
    
    if(!imageUrl) {
      return;
    }

    fetch(imageUrl)
    .then((response) => {
      return response.blob();
    })
    .then(blob => {
      const url = URL.createObjectURL(blob);

      const thumbnailMedia = {
        id: nanoid(6),
        entityType: 'media-preview',
        type: media.mediaType,
        full: url,
        thumbnail: url,
        width: media.width,
        height: media.height,
        contentType: 'image/jpeg',
      } as MediaFile

      setSelectedMedia(thumbnailMedia);

    }).catch(error => {
      console.error(error);
    });

    setEditAction("default");
    setEditThumbnail(false);
  }

  const handleCroppedThumbnail = (crop) => {
    setCropData(crop);
  };

  const processImageData = (url: any) => {
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const image = new Image();
    image.crossOrigin="anonymous";
    image.src = url;

    var dataUrl = "";

    image.onload = () => {
      if (cropData) {
  
        canvas.width = cropData.width;
        canvas.height = cropData.height;
  
        ctx.drawImage(image, cropData.x, cropData.y, cropData.width, cropData.height, 0, 0, cropData.width, cropData.height);
  
        dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        processImageDataToMedia(dataUrl);
      }
    }
  };

  const handleConfirm = async () => {
    
    if(!editThumbail){

      const mediaFile = selectedMedia as MediaFile;
      const blob = await fetch(selectedMedia!.full!).then(r => r.blob());
      const file = new File([blob], 'thumbnail.png', { type: mediaFile.contentType || blob.type });

      dispatch(uploadMedia({
        id: selectedMedia?.id as string,
        media: {
          ...selectedMedia,
          file: file
        } as MediaFile
      }))
    }
    
    if (editAction === "crop") {

      // create the cropped image and upload it
      processImageData(selectedMedia?.full);

      let thumbnail = selectedMedia;
      thumbnail = {
        ...thumbnail,
        id: thumbnail?.id || nanoid(6),
        type: thumbnail?.type as "image",
      }
      setSelectedMedia(thumbnail)
    } 

    setEditAction("default");
    setEditThumbnail(false);
    setSelectedMediaSource("");
  }

  const handleCancel = () => {
    if(!editThumbail){
      onClose();
    }
    if(editAction === "crop"){
      setEditThumbnail(false);
    }
    if(editAction === "edit"){
      setEditThumbnail(false);
    }
    setEditAction("default");
    setSelectedMediaSource("");
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    var fileSizeInMB = parseFloat((file.size / (1024 * 1024)).toFixed(1));

    if (fileSizeInMB > 5) {
      setFileSizeError(true);
      return;
    }

    var thumbnailMedia = await parseMediaMetadata(file);
    if (!thumbnailMedia) return;

    thumbnailMedia = {
      ...thumbnailMedia,
      id: nanoid(6),
      contentType: file.type,
    } as MediaFile

    setSelectedMedia(thumbnailMedia);

    setSelectedMedia(thumbnailMedia);
    setCropData({ x: 0, y: 0, width: thumbnailMedia.width ?? 0, height: thumbnailMedia.height ?? 0 });
    setEditAction("default");
    setEditThumbnail(false);
  }

  const handleBackClick = () => {
    setEditThumbnail(false);
    setEditAction("default");
    setSelectedMediaSource("");
  }

  const getSliderTitle = () => {
    const accept = "image/*";
    const disabled = uploading;
    return (
      <div className={styles.sliderTitle}>
        {editAction === "edit" && (
          <>
            <div className={styles.titleContainer}>
              <i
                className={cx("paperflite-left-arrow", styles.backIcon)}
                onClick={() => handleBackClick()}
              ></i>
              <span>customize your thumbnail</span>
            </div>
            <button className={cx(styles.fileUpload)} 
              disabled={disabled}>
                <i className={cx(styles.icon, "paperflite-upload")}></i>
                <span className={styles.title}>upload</span>
              <input type="file" accept={accept} disabled={disabled} onChange={handleFileChange}></input>
            </button>
          </>
        )}

        {editAction !== "edit" &&
          <span>customize your thumbnail</span>
        }
      </div>
    );
  };

  const handleSelectedMediaSource = (source: string) => {
    setSelectedMediaSource(source)
  }

  const processImageDataToMedia = (imageData: string) => {
    fetch(imageData)
    .then((response) => {
      return response.blob();
    })
    .then(async(blob) => {
      const url = URL.createObjectURL(blob);  
      const mediaFile = await parseMediaMetadata(new File([blob], 'thumbnail.png', { type: blob.type }));
      const thumbnailMedia = {
        id: nanoid(6),
        entityType: 'media-preview',
        type: mediaFile?.type,
        full: url,
        thumbnail: url,
        width: mediaFile?.width,
        height: mediaFile?.height,
        contentType: blob.type,
      } as MediaFile

      setSelectedMedia(thumbnailMedia);
    });
  }

  const handleCaptureFrame = (image) => {
    processImageDataToMedia(image);
  }

  const handlePickFromPage = (icon) => {
      setEditAction("default");
      setEditThumbnail(false);
      const thumbnail: Media = {
        id: nanoid(6),
        type: 'image',
        key: icon.key,
        full: icon.full,
        thumbnail: icon.thumbnail,
      }
      setSelectedMedia(thumbnail);
    }

  const handleClick = (action: string) => {
    setEditAction(action);
    setEditThumbnail(true);
  }

  useEffect(() => {
    let thumbnail = selectedMedia;
    thumbnail = {
      ...thumbnail,
      id: thumbnail?.id || nanoid(6),
      thumbnail: asset.icon?.thumbnail,
      full: asset.icon?.full, 
      key: asset.icon?.key,
      type: "image"
    }
    setSelectedMedia(thumbnail)
  }, [])


  useEffect(() => {

    if(updated) {
      onClose();
    }

    if(mediaUploaded) {
      let updateRequest:any = {
        id: asset.id,
        icon: media?.id
      }
  
      updateAssetRequest({
        id: asset.id,
        request: updateRequest
      }).then((response) => {
        trackAssetEdited({
          sourceCta: ASSET_DETAIL, 
          response: response, 
           ...(selectedCard && { sectionType: getSectionType(selectedCard.key) })
        });
      });

      if (selectedMedia?.id) {
        dispatch(invalidateMediaUpload(selectedMedia.id));
      }
    }
  }, [mediaUploaded, updated]);

  useEffect(() => {
    if(fileSizeError) {
      toast(<Toast.Failure message={`Please upload a file of size less than 5MB`} />, {
        autoClose: 100000
      })
    }
  }, [fileSizeError]);

  const widthStyles = selectedMediaSource === 'pick-from-video' ? '53.75rem' : editAction === 'edit' ? '33.75rem' : '23.75rem';
  
  return (
    <Slider.Dialog
    className={styles.editGroupDetailsContainer}
    width={widthStyles}
    show={show}
    onClose={onClose}
  >
    <Slider.Header
      title={getSliderTitle()}
      message={
        editAction === "edit"
          ? ""
          : "pick your preferred hub layout to match your styles"
      }
    ></Slider.Header>
    <Slider.Body>
      {editAction === "default" && (
      <div className={styles.body}>
        
        <Thumbnail
          media={selectedMedia}
          className={`${styles["asset"]}`}
          alt="thumbnail"
          defaultImage={DefaultAssetIcon}
        />
        <EditThumbnailToolbar show={true} handleClick={(action) => handleClick(action)} />
      </div>
      )}

      {editAction === "edit" && (
        <MediaLibraryChooser
          entityType="asset"
          onChange={handleThumbnailChange}
          columns={3}
          handleSelectedMediaSource={handleSelectedMediaSource}
          handleCaptureFrame={handleCaptureFrame}
          handlePickFromPage={handlePickFromPage}
        />
      )}

      {editAction === "crop" && (
        <Crop
          enabled={true}
          icon={selectedMedia?.full}
          loading={false}
          onCrop={handleCroppedThumbnail}
          headerDimension={{
            height: headerBounds.height,
            width: headerBounds.width,
          }}
          cropperDimensions={{
            width: "15rem",
            height: "19.375rem",
          }}
        />
      )}
    </Slider.Body>

    {(editAction === "default" ||
      editAction === "crop" ||
      selectedMediaSource === "pick-from-video") && (
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={(updating || mediaUploading) ? "Updating..." : "Update"}
          variant="primary"
          disabled={updating || mediaUploading}
          onClick={() => handleConfirm()}
        />
        <Button
          text="Cancel"
          variant="secondary"
          onClick={handleCancel}
        />
      </Slider.Actions>
    )}
  </Slider.Dialog>
  );
}

interface EditThumbnailToolbarProps {
  show: boolean;
  handleClick: (action: string) => void;
}

const EditThumbnailToolbar: React.FC<EditThumbnailToolbarProps> = ({ show, handleClick }) => {
  const transitions = useTransition(show, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 }
  });

  return transitions(
    (style, item) =>
      item && (
        <animated.div style={style} className={cx(styles.toolbar)}>
          <Tooltip content="Edit" placement='top'>
            <div>
              <IconButton icon="paperflite-pen" onClick={() => handleClick("edit")} />
            </div>
          </Tooltip>
          <Tooltip content="Crop" placement='top'>
            <div>
              <IconButton icon="paperflite-crop" onClick={() => handleClick("crop")} />
            </div>
          </Tooltip>
        </animated.div>
      ) 
  );
};

interface IconButtonProps {
  icon: string;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const IconButton: React.FC<IconButtonProps> = ({ icon, onClick, disabled }) => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled && onClick) {
      onClick(event);
    }
  };

  return (
    <div className={cx(styles.item, styles.iconItem, {
      [styles.disabled]: disabled,
    })} onClick={handleClick}>
      <i className={cx(styles.icon, icon)}></i>
    </div>
  );
}

export default EditAssetThumbnail


